import {CONTACT_EMAIL, LegalDocument, OPERATOR_NAME} from './types';

/**
 * Политика конфиденциальности — что сервис собирает, зачем и кому передаёт.
 *
 * Документ отвечает на вопрос «что происходит с моими данными» человеческим
 * языком; формальные основания и сроки по 152-ФЗ — в Политике обработки
 * персональных данных. Текст требует юридической проверки (requirements.md §7).
 */
export const PRIVACY_POLICY: LegalDocument = {
    type: 'PRIVACY_POLICY',
    title: 'Политика конфиденциальности',
    shortTitle: 'Конфиденциальность',
    version: '1.0',
    effectiveOn: '2026-08-26',
    slug: 'privacy',
    intro:
        'Этот документ объясняет, какие данные собирает сервис Clothes, зачем они нужны, ' +
        'кому передаются и как их контролировать. Формальные основания обработки и сроки ' +
        'хранения приведены в Политике обработки персональных данных.',
    sections: [
        {
            id: 'privacy-principles',
            title: '1. Принципы',
            blocks: [
                {
                    kind: 'list',
                    items: [
                        'Мы собираем только те данные, без которых функция не работает.',
                        'Гардероб одного пользователя не виден другому, пока владелец сам не выдал права доступа.',
                        'Ваш контент не используется для обучения моделей машинного обучения.',
                        'Мы не продаём данные и не передаём их рекламным сетям.',
                        'Функции, требующие передачи данных вовне, можно отключить в настройках приватности.',
                    ],
                },
            ],
        },
        {
            id: 'privacy-what',
            title: '2. Какие данные собираются',
            blocks: [
                {
                    kind: 'table',
                    head: ['Категория', 'Состав', 'Зачем'],
                    rows: [
                        ['Учётные данные', 'адрес электронной почты, отображаемое имя, хеш пароля', 'вход в сервис, восстановление доступа, уведомления о безопасности'],
                        ['Контент гардероба', 'фотографии вещей, названия, категории, бренды, цвета, размеры, цены, заметки', 'основная функция — учёт вещей'],
                        ['Поведение в сервисе', 'история носки и стирок, оценки образов, действия в разборе гардероба', 'аналитика гардероба, рекомендации, статистика'],
                        ['Настройки', 'предпочтения, состояние согласий, выбранная тема оформления', 'персонализация и соблюдение ваших решений'],
                        ['Технические данные', 'IP-адрес, User-Agent, журналы обращений к API, идентификаторы сессий', 'безопасность, расследование инцидентов, диагностика'],
                        ['Данные подписки', 'тариф, лимиты, история их расходования', 'предоставление платных функций'],
                    ],
                },
                {
                    kind: 'p',
                    text:
                        'Сервис не запрашивает данные специальных категорий (о здоровье, убеждениях, ' +
                        'биометрию). Если такие сведения окажутся в загруженных фотографиях или заметках, ' +
                        'они обрабатываются как обычный контент — их состав определяете вы.',
                },
            ],
        },
        {
            id: 'privacy-cookies',
            title: '3. Файлы cookie и локальное хранилище',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Сервис использует технические cookie и локальное хранилище браузера. Без них ' +
                        'невозможен вход и сохранение пользовательских настроек.',
                },
                {
                    kind: 'list',
                    items: [
                        'Сессионные — поддержание входа в систему и защита от подделки запросов.',
                        'Настройки интерфейса — выбранная тема, состояние боковой панели, свёрнутые разделы.',
                        'Согласия — отметка о том, что баннер о cookie уже показан.',
                    ],
                },
                {
                    kind: 'p',
                    text:
                        'Рекламные и аналитические cookie третьих лиц не устанавливаются. ' +
                        'Сторонние трекеры и пиксели в сервисе не используются.',
                },
            ],
        },
        {
            id: 'privacy-ai',
            title: '4. Передача данных AI-провайдерам',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'AI-функции — распознавание вещей по фотографии, подбор образов, анализ гардероба ' +
                        'и покупок — выполняются с привлечением внешних поставщиков моделей машинного обучения. ' +
                        'Для этого им передаётся содержимое конкретного запроса.',
                },
                {
                    kind: 'list',
                    items: [
                        'Передаётся только то, что нужно для запроса: фотография вещи или её описание, характеристики гардероба, текст вашего вопроса стилисту.',
                        'Адрес электронной почты, имя и идентификаторы учётной записи в запросы не включаются.',
                        'Часть провайдеров расположена за пределами Российской Федерации — это трансграничная передача, её условия описаны в Политике обработки персональных данных.',
                        'Вы можете подключить собственный ключ доступа к AI-провайдеру: тогда запросы уходят от вашего имени и на ваших условиях с этим провайдером.',
                        'AI-профилирование и обработку фотографий можно полностью отключить в настройках приватности.',
                    ],
                },
                {
                    kind: 'p',
                    text:
                        'Актуальный перечень задействованных поставщиков приведён в Политике обработки ' +
                        'персональных данных и обновляется при изменении состава.',
                },
            ],
        },
        {
            id: 'privacy-sharing',
            title: '5. Кому ещё доступны данные',
            blocks: [
                {
                    kind: 'list',
                    items: [
                        'Другим пользователям — только гардероб и только в объёме прав, которые вы им выдали. Права выдаются точечно и отзываются в любой момент.',
                        'Поставщикам инфраструктуры — хостинг, объектное хранилище, отправка электронной почты. Они обрабатывают данные по поручению Оператора и не вправе использовать их иначе.',
                        'Государственным органам — по мотивированному запросу в случаях, прямо предусмотренных законом.',
                    ],
                },
                {
                    kind: 'p',
                    text: 'Продажа, аренда и обмен данными не осуществляются ни при каких условиях.',
                },
            ],
        },
        {
            id: 'privacy-security',
            title: '6. Как данные защищены',
            blocks: [
                {
                    kind: 'list',
                    items: [
                        'Передача данных — только по защищённому соединению (HTTPS).',
                        'Пароли не хранятся в открытом виде; аутентификация вынесена в отдельный сервис управления доступом.',
                        'Чувствительные поля (в том числе ключи доступа к AI-провайдерам) хранятся в зашифрованном виде.',
                        'Доступ к данным разграничен по ролям и правам; попытки доступа к чужим данным отклоняются на сервере, а не только скрываются в интерфейсе.',
                        'Отказы в доступе и неудачные попытки аутентификации фиксируются в журнале аудита.',
                        'В журналах и системах мониторинга пароли, токены и одноразовые коды маскируются.',
                    ],
                },
                {
                    kind: 'p',
                    text:
                        'Ни одна мера не даёт абсолютной гарантии. При инциденте, затрагивающем ваши данные, ' +
                        `мы уведомим вас по адресу почты и опубликуем информацию в сервисе.`,
                },
            ],
        },
        {
            id: 'privacy-control',
            title: '7. Что вы можете сделать со своими данными',
            blocks: [
                {
                    kind: 'list',
                    items: [
                        'Посмотреть и изменить — в профиле и карточках вещей.',
                        'Выгрузить гардероб в машиночитаемом формате — в настройках профиля.',
                        'Отозвать отдельные согласия (AI-профилирование, обработка фотографий, рассылки) — в центре приватности.',
                        'Удалить отдельные вещи или весь гардероб — в интерфейсе гардероба.',
                        'Удалить учётную запись целиком — в настройках профиля или по обращению на ' + CONTACT_EMAIL + '.',
                    ],
                },
                {
                    kind: 'p',
                    text:
                        'Отзыв согласия не влияет на законность обработки, совершённой до отзыва, ' +
                        'и не отменяет обработку, которая ведётся на других основаниях (например, ' +
                        'исполнение договора или требования закона о сроках хранения).',
                },
            ],
        },
        {
            id: 'privacy-children',
            title: '8. Возрастные ограничения',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Сервис не предназначен для лиц младше 14 лет. Если нам станет известно, что ' +
                        'учётная запись создана лицом младше указанного возраста без согласия законного ' +
                        'представителя, она будет удалена вместе с данными.',
                },
            ],
        },
        {
            id: 'privacy-changes',
            title: '9. Изменения политики',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Политика может обновляться. Каждая редакция имеет номер версии и дату вступления ' +
                        'в силу. О существенных изменениях мы уведомим в сервисе или по электронной почте ' +
                        'до того, как они вступят в силу.',
                },
            ],
        },
        {
            id: 'privacy-contacts',
            title: '10. Контакты',
            blocks: [
                {
                    kind: 'p',
                    text:
                        `Оператор: ${OPERATOR_NAME}. Вопросы о персональных данных и запросы ` +
                        `на их удаление или выгрузку — на ${CONTACT_EMAIL}. Ответ направляется в сроки, ` +
                        'установленные законодательством.',
                },
            ],
        },
    ],
};
